import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Layout, Select, Tabs, WordCloud } from '@utilities';

import NoDataPlaceholder from '../NoDataPlaceholder';
import ConceptImageList from '../ConceptImageList';
import OpenEndedTable from './components/OpenEndedTable';

const getSequentialSegmentId = ({ conceptValue, segmentValue }) => {
  if (conceptValue == null || !segmentValue) return segmentValue;
  return `${segmentValue}_${conceptValue}`;
};

const OpenEnded = ({
  compareByGroups = [],
  conceptImages,
  data,
  onOpenConceptImage,
  question,
  renderResponseTextSummary,
}) => {
  const conceptOptions = useMemo(
    () =>
      (question?.concepts || [])
        .filter(({ id, name }) => id !== null && id !== undefined && name)
        .map(({ id, name }) => ({ label: name, value: id })),
    [question?.concepts]
  );
  const isSequentialMonadicOpenEnded = conceptOptions.length > 0;
  const segmentOptions = useMemo(() => {
    if (!isSequentialMonadicOpenEnded) {
      return data?.segments?.map(({ id, name }) => ({ label: name, value: id })) || [];
    }

    return compareByGroups
      .filter(({ id, name }) => id != null || name != null)
      .map(({ id, name }) => {
        const value = String(name ?? id);
        return { label: name ?? id, value };
      });
  }, [compareByGroups, data?.segments, isSequentialMonadicOpenEnded]);
  const [selectedConceptValue, setSelectedConceptValue] = useState(
    () => conceptOptions[0]?.value ?? null
  );
  const [selectedSegmentValue, setSelectedSegmentValue] = useState(() => segmentOptions[0]?.value);
  const selectedConcept =
    conceptOptions.find(({ value }) => value === selectedConceptValue) || conceptOptions[0] || null;
  const activeSegmentValue =
    segmentOptions.find(({ value }) => value === selectedSegmentValue)?.value ||
    segmentOptions[0]?.value;
  const selectedConceptImageIndex = conceptImages?.findIndex(
    ({ id }) => String(id) === String(selectedConceptValue)
  );
  const selectedConceptImage =
    selectedConceptImageIndex >= 0 ? conceptImages[selectedConceptImageIndex] : null;

  useEffect(() => {
    setSelectedSegmentValue((currentValue) =>
      segmentOptions.some(({ value }) => value === currentValue)
        ? currentValue
        : segmentOptions[0]?.value
    );
  }, [segmentOptions]);

  useEffect(() => {
    setSelectedConceptValue(conceptOptions[0]?.value ?? null);
  }, [conceptOptions]);

  // For non-SM questions, segment id === selected tab value. For SM questions,
  // derived from tab value and concept id: `${segmentValue}_${conceptValue}`.
  const selectedSegmentId = isSequentialMonadicOpenEnded
    ? getSequentialSegmentId({
        conceptValue: selectedConceptValue,
        segmentValue: activeSegmentValue,
      })
    : activeSegmentValue;
  const getSegmentById = (segmentId) =>
    data?.segments?.find((segment) => String(segment.id) === String(segmentId));
  const selectedSegment = getSegmentById(selectedSegmentId);

  const wordCloudData =
    selectedSegment?.chart?.series[0]?.data?.map((point) => ({ ...point })) || [];

  return (
    <Layout.Flex.Column>
      {conceptOptions.length > 0 && (
        <Select
          data-testid="open-ended-concept-select"
          label="Concept"
          onChange={(option) => setSelectedConceptValue(option?.value ?? null)}
          options={conceptOptions}
          value={selectedConcept}
        />
      )}
      {selectedConceptImage && onOpenConceptImage && (
        <ConceptImageList
          // The items list contains only the selected image,
          // so its click index is always 0.
          // Use matching index from full conceptImages list for lightbox.
          items={[selectedConceptImage]}
          onOpen={() => onOpenConceptImage(selectedConceptImageIndex)}
        />
      )}
      <Tabs
        onChange={setSelectedSegmentValue}
        options={segmentOptions}
        value={activeSegmentValue}
      />
      {selectedSegment ? (
        <>
          <Accordion isDefaultOpen label="Show/Collapse Word Cloud">
            <WordCloud data={wordCloudData} key={selectedSegment.id} />
          </Accordion>
          {renderResponseTextSummary({ question, segment: selectedSegment })}
          <OpenEndedTable
            columns={selectedSegment.table.columns}
            data={selectedSegment.table.data}
          />
        </>
      ) : (
        <NoDataPlaceholder />
      )}
    </Layout.Flex.Column>
  );
};

OpenEnded.propTypes = {
  compareByGroups: PropTypes.arrayOf(PropTypes.object),
  conceptImages: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.object,
  onOpenConceptImage: PropTypes.func,
  question: PropTypes.object,
  renderResponseTextSummary: PropTypes.func,
};

export default OpenEnded;
